import React, { useCallback, useEffect, useState } from "react";
import { PhoneCall, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../software/api";
import { localNumber } from "./IncomingCalls";

const STATUS = {
  ringing: "bg-emerald-100 text-emerald-800", answered: "bg-sky-100 text-sky-800",
  missed: "bg-rose-100 text-rose-800", dismissed: "bg-slate-200 text-slate-700",
};
const when = (iso) => new Date(iso).toLocaleString("en-AU", { weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });

export function CallHistory({ onOpenClient, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [num, setNum] = useState("");
  const load = useCallback(() => api("GET", "/calls/history").then(setRows).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const openCall = async (c) => {
    if (c.status === "missed" && !c.viewed_by) { try { await api("POST", `/calls/${c.id}/viewed`); window.dispatchEvent(new Event("aqualux:calls-changed")); } catch { /* still open the file */ } }
    onOpenClient(c.client_id);
  };
  const test = async (random) => {
    try {
      const c = await api("POST", "/calls/test", { number: random ? "" : num, random_client: random });
      toast.success(`Test call from ${localNumber(c.number)} sent to every office screen`);
      window.dispatchEvent(new Event("aqualux:calls-changed")); load();
    } catch (e) { toast.error(e.message || "Could not send test call."); }
  };
  return (
    <div data-testid="call-history">
      <div className="flex flex-wrap items-end gap-2 mb-4 rounded-md border border-slate-300 bg-white p-3">
        <div className="text-xs font-bold text-slate-700 w-full">Test the Caller ID pop-up (fakes an incoming call)</div>
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Phone number e.g. 0412 345 678" data-testid="call-test-number"
          className="h-8 w-64 rounded border border-slate-300 px-2 text-sm" />
        <button onClick={() => test(false)} disabled={!num.trim()} data-testid="call-test-send" className="h-8 inline-flex items-center gap-1.5 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-bold px-3"><PhoneCall className="h-3.5 w-3.5" /> Ring this number</button>
        <button onClick={() => test(true)} data-testid="call-test-random" className="h-8 inline-flex items-center gap-1.5 rounded bg-[#123a63] hover:bg-[#1f4e86] text-white text-xs font-bold px-3"><Shuffle className="h-3.5 w-3.5" /> Ring from a random client</button>
      </div>
      {isAdmin && <PhoneLink />}
      <table className="w-full text-sm bg-white border border-slate-300" data-testid="call-history-table">
        <thead className="bg-slate-200 text-slate-700 text-xs uppercase"><tr>
          <th className="text-left px-2 py-1.5">When</th><th className="text-left px-2 py-1.5">Number</th><th className="text-left px-2 py-1.5">Caller</th>
          <th className="text-left px-2 py-1.5">Status</th><th className="text-left px-2 py-1.5">Taken by</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-200 hover:bg-sky-50" data-testid={`call-row-${c.id}`}>
              <td className="px-2 py-1 whitespace-nowrap">{when(c.created_at)}</td>
              <td className="px-2 py-1 font-mono2">{localNumber(c.number) || "Withheld"}</td>
              <td className="px-2 py-1">{c.client_id
                ? <button onClick={() => openCall(c)} data-testid={`call-row-client-${c.id}`} className="text-[#1f4e86] font-semibold hover:underline text-left">{c.client_name}</button>
                : <span className="text-slate-500">Unknown Caller</span>}{c.source === "test" && <span className="ml-1 text-[10px] text-slate-400">(test)</span>}
                {!c.client_id && c.status === "missed" && !c.viewed_by && <button onClick={() => api("POST", `/calls/${c.id}/viewed`).then(() => { load(); window.dispatchEvent(new Event("aqualux:calls-changed")); })} data-testid={`call-row-mark-viewed-${c.id}`} className="ml-2 text-[11px] font-bold text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded px-1.5">Mark viewed</button>}</td>
              <td className="px-2 py-1" data-testid={`call-row-status-${c.id}`}>{c.status === "missed" && c.viewed_by
                ? <span className="rounded px-1.5 py-0.5 text-[11px] font-bold bg-amber-100 text-amber-800" title={when(c.viewed_at)}>Missed – Viewed by {c.viewed_by} · {when(c.viewed_at)}</span>
                : <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold capitalize ${STATUS[c.status] || ""}`}>{c.status}</span>}</td>
              <td className="px-2 py-1 text-slate-600">{c.answered_by || ""}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-500">No calls yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PhoneLink() {
  const [s, setS] = useState(null);
  useEffect(() => { api("GET", "/calls/setup").then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const url = `${process.env.REACT_APP_BACKEND_URL}${s.path}?token=${s.token}`;
  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="call-phone-link">
      <p className="font-bold mb-1">Phone system link (for Business Telecoms)</p>
      <p className="mb-1">Ask them to send each incoming call to this address (POST, JSON <span className="font-mono2">{`{"number":"0412345678"}`}</span>):</p>
      <p className="font-mono2 break-all bg-white border border-amber-200 rounded px-2 py-1 select-all" data-testid="call-phone-link-url">{url}</p>
    </div>
  );
}
